import { Alert, Anchor, Box, Card, Center, Container, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import Metadata from '../components/Metadata';
import Spinner from '../components/Spinner';
import { Page } from '../constants/pageTitles';
import useFetch from '../helpers/useFetch';
import mainLogo from '../logo.png';

// Shopify has already taken the merchant's approval by the time they land here;
// what we are waiting for is our own side catching up, via the webhook the
// connector turns into an active domainSubscription. That is usually seconds.
// It is bounded because the alternative is a spinner with no end: if the webhook
// never arrives the merchant would sit here forever, with nothing said and
// nowhere to go.
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 60000;

const SubscriptionsConfirmPage = () => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const { domainId, accountId } = useParams();

  const requested = queryString.parse(location.search).subscriptionsToConfirm;
  // Memoised on the raw string, not re-split per render: a fresh array identity
  // every render would make `poll` unstable, which would restart the effect on
  // every render and turn a 1s poll into a request storm.
  //
  // Split defensively too - reaching this route without the parameter used to
  // throw on `undefined.split`, white-screening the page rather than degrading.
  const subscriptionsToConfirm = useMemo(() => (requested ? requested.split(',') : []), [requested]);

  const [timedOut, setTimedOut] = useState(false);
  const deadline = useRef(null);

  const poll = useCallback(async () => {
    const { domain } = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}`);

    const confirmed = subscriptionsToConfirm.filter((id) =>
      domain.domainSubscriptions.some((s) => s.id.toString() === id && s.status === 'active')
    );

    if (confirmed.length < subscriptionsToConfirm.length) {
      return false;
    }

    // Onboarding is not finished until the merchant starts their trial, and the
    // subscription they just approved is one step of it - so hand them back to
    // the wizard, which resumes at the first step still outstanding (the theme
    // app embed, now that terms and subscription are done). Sending everyone to
    // the dashboard instead only looked right because DomainPage bounced
    // merchants back into the wizard behind the scenes; that never applied to
    // @releva.ai users, who were simply stranded on the dashboard mid-onboarding.
    const next = domain.integrationActive ? 'analytics' : 'activate';
    navigate(`/accounts/${domain.accountId}/domains/${domain.id}/${next}`);
    return true;
  }, [fetch, accountId, domainId, navigate, subscriptionsToConfirm]);

  useEffect(() => {
    if (subscriptionsToConfirm.length === 0) {
      setTimedOut(true);
      return undefined;
    }

    let cancelled = false;
    let timer;
    deadline.current = deadline.current ?? Date.now() + POLL_TIMEOUT_MS;

    const tick = async () => {
      if (cancelled) {
        return;
      }
      try {
        if (await poll()) {
          return;
        }
      } catch {
        // A failed read says nothing about whether the subscription landed, so
        // keep waiting rather than reporting a failure that may not exist.
      }
      if (cancelled) {
        return;
      }
      if (Date.now() > deadline.current) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [poll, subscriptionsToConfirm]);

  return (
    <div>
      <Metadata title={`${Page['activateDomain']}`} />
      <Center pt={60} h="100%">
        <Container size="xs" w="100%">
          <Card withBorder p="lg">
            <Box ta="center" mb="lg">
              <img src={mainLogo} alt="Releva" style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
            {timedOut ? (
              <>
                <Title order={3} ta="center" mb="md">
                  We could not confirm your subscription
                </Title>
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm">
                    If you approved the plan in Shopify, the confirmation may still be on its way — it usually arrives
                    within a minute. Nothing has been lost.
                  </Text>
                </Alert>
                <Text ta="center" mt="md">
                  <Anchor href={`#/accounts/${accountId}/domains/${domainId}/activate`}>
                    Return to setup and try again
                  </Anchor>
                </Text>
              </>
            ) : (
              <>
                <br />
                <br />
                <Title order={3}>Confirming your subscriptions, please stand by...</Title>
                <br />
                <Spinner />
              </>
            )}
          </Card>
        </Container>
      </Center>
    </div>
  );
};

export default SubscriptionsConfirmPage;
